import {
  hasLoadedAlignmentInProject,
  useApiServices,
  useDataContext,
  useMapTileConfig,
  useCapabilityRegistryContext,
  useThemeContext,
  useProjectSettings,
  useFeatureExtractionContext,
  getVisiblePointCloudIds,
  viewerPreferenceMaterialPhong,
} from '@mobile-mapping/providers';
import { DrawingTool } from '@trimble/geospatial';
import { CadEditingCapability } from './feature-extraction/CadEditingCapability';
import { CadEditingToolbar } from './feature-extraction/CadEditingToolbar';
import { useCadEditingPane } from './feature-extraction/useCadEditingPane';
import { fetchGeoDataFilesByCsib } from '@data-access/coordinates';
import {
  PaneContents,
  ViewMode,
  ViewModeCapabilityCoordinator,
  viewModeEquals,
} from '@mobile-mapping/web3d';
import { Web3DViewer } from '@technology/web3d';
import { useKeyDownEvent } from '@solid-primitives/keyboard';
import { PaneElementProps } from '@trimble/pane-layout';
import { useHover, useLogger } from '@trimble/solidjs-utils';
import { AlignmentNavigationToolbar } from '@trimble/alignment-navigation';
import {
  BasicButton,
  CameraControlSelector,
  TrbToolbar,
  ToolbarPositioner,
  Anchor,
} from '@trimble/ui-components/solidjs';
import { t } from '@trimble/i18n';
import { LengthUnit } from '@fulcrum/lib';
import { PaneToolbarVisibilityControl } from './components/PaneToolbarVisibilityControl';
import { ObjectSelectionToolbarContainer } from './components/ObjectSelectionToolbarContainer';
import { Web3DViewerWrapper } from '@trimble/utilities';
import { CapabilitiesCollection } from '@trimble/web3d';
import {
  AlignmentNavigationCapability,
  FreeViewCapability,
  ImageCapabilityBase,
  ImagePointcloudCapability,
  MarkupCapability,
  SelectionActionsCapability,
  StationsNavigationCapability,
} from '@trimble/web3d-capabilities';
import {
  MarkupsCapabilityProvider,
  PointCloudCapabilityProvider,
  StationsCapabilityProvider,
  TrimBimCapabilityProvider,
} from '@trimble/web3d-providers';
import {
  createEffect,
  createMemo,
  createSignal,
  JSX,
  on,
  onCleanup,
  onMount,
  Show,
} from 'solid-js';

import { createRotatePanoramasAction } from './station-load/rotate-panoramas-action';
import { useStationLoadContext } from './station-load/station-load-context';

import {
  EmptyViewerState,
  EmptyViewerStateProps,
} from './components/EmptyViewerState';
import { PaneBottomBar } from './components/PaneBottomBar';
import { applyViewerPreferences } from '@trimble/viewer-preferences';
import {
  createPaneViewerSession,
  type PaneViewerSession,
} from './pane-viewer-session';

export const PaneViewer = (
  props: PaneElementProps<PaneContents, ViewMode>
): JSX.Element => {
  const logger = useLogger().getLogger('PaneViewer');

  const [viewModeCapCoord, setViewModeCapCoord] =
    createSignal<ViewModeCapabilityCoordinator>();

  const [emptyViewerStateProps, setEmptyViewerStateProps] =
    createSignal<EmptyViewerStateProps>();

  let session: PaneViewerSession | undefined;

  const viewModeFromLayout = createMemo(
    (prev: ViewMode | undefined): ViewMode => {
      const activeLayout = props.layoutConfig.layouts.layouts.find(
        (l) => l.label === props.layoutConfig.layouts.activeLayoutLabel
      );
      if (activeLayout === undefined) {
        throw new Error('Active layout must be selected');
      }

      const activeRow = activeLayout.paneRows[props.layoutConfig.rowIdx];
      const activeCell = activeRow.cells[props.layoutConfig.cellIdx];

      const next: ViewMode =
        activeCell.contentConfig.type !== 'Image'
          ? {
              type: activeCell.contentConfig.type,
            }
          : {
              type: activeCell.contentConfig.type,
              cameraIndex: activeCell.contentConfig.cameraIndex,
            };

      if (!prev) return next;
      return viewModeEquals(prev, next) ? prev : next;
    }
  );

  createEffect(
    on(
      () => props.layoutConfig.layouts.activeLayoutLabel,
      () => {
        const vm = viewModeFromLayout();
        const coord = viewModeCapCoord();
        if (coord) {
          coord.setViewMode(vm);
        }
      },
      { defer: true }
    )
  );

  createEffect(
    on(
      () => viewModeCapCoord()?.viewMode(),
      (vm) => {
        if (!vm) return;

        const activeLabel = props.layoutConfig.layouts.activeLayoutLabel;

        props.layoutConfig.setLayouts(
          'layouts',
          (layouts) => layouts.label === activeLabel,
          'paneRows',
          props.layoutConfig.rowIdx,
          'cells',
          props.layoutConfig.cellIdx,
          'contentConfig',
          vm
        );
      }
    )
  );

  const { web3dSettings, preferenceSettings } = useThemeContext();
  const {
    dataLoadingService,
    dataState,
    currentLoadedCsib,
    currentLoadedCenter,
    currentLoadedBounds,
    loadedCsibs,
    loadedCenters,
  } = useDataContext();
  const { formattingSettings } = useProjectSettings();
  const { tilesetPrefetchContext } = useStationLoadContext();

  let placeholder!: HTMLDivElement;
  let viewerWrapper: Web3DViewerWrapper | undefined;

  const { imagingApi, coordinatesClient } = useApiServices();
  const mapTileConfig = useMapTileConfig();

  const [viewer, setViewer] = createSignal<Web3DViewer>();

  const viewModeType = createMemo(() => viewModeCapCoord()?.viewMode().type);

  const featureExtraction = useFeatureExtractionContext();
  const featureExtractionLogger = useLogger().getLogger('FeatureExtraction');

  // Only CSIB-bearing clouds — no cross-cloud CSIB substitution.
  const pointCloudRoots = createMemo(() => {
    const csibs = loadedCsibs();
    const centers = loadedCenters();
    return (getVisiblePointCloudIds(dataState) ?? []).flatMap((id) => {
      const csib = csibs.get(id);
      const center = centers.get(id);
      return csib && center ? [{ id, csib, center }] : [];
    });
  });

  const sceneCsib = createMemo(() => {
    const roots = pointCloudRoots();
    if (roots.length === 0) {
      return undefined;
    }
    const primary = [...roots].sort((a, b) => a.id.localeCompare(b.id))[0];
    return primary?.csib;
  });

  const cadToolsDisabled = createMemo(
    () => featureExtraction.selectedLayer() === undefined
  );

  const allowMarkups = createMemo(() => {
    return viewModeType() !== '2D';
  });

  const capabilityRegistry = useCapabilityRegistryContext().capabilityRegistry;
  /* eslint-disable solid/reactivity -- rowIdx/cellIdx are static for the pane's lifetime */
  const capRegContainer = capabilityRegistry.registerPaneContainer(
    undefined,
    props.layoutConfig.rowIdx,
    props.layoutConfig.cellIdx
  );
  /* eslint-enable solid/reactivity */

  const {
    cadEditingMode,
    cadEditingSession,
    isCadEditingEligible,
    toggleCadEditingMode,
  } = useCadEditingPane({
    featureExtraction,
    featureExtractionLogger,
    isEditingEligible: featureExtraction.isEditingEligible,
    viewModeType,
    sceneCsib,
    pointCloudRoots,
    getViewerWrapper: () => viewerWrapper,
    fetchGeoDataFiles: (csib64) =>
      fetchGeoDataFilesByCsib(coordinatesClient, csib64),
    onCadEditingEnabled: () => {
      const capability = capRegContainer.get(CadEditingCapability)();
      capability?.enable();
      capability?.activate();
    },
    onCadEditingDisabled: () => {
      void capRegContainer.get(CadEditingCapability)()?.disable();
    },
  });

  const allowMarkupEditing = createMemo(
    () =>
      viewModeType() === '3D' &&
      capRegContainer
        .get(StationsNavigationCapability)()
        ?.selectedStationIndex() == null
  );
  const selectionActionsCapability = ():
    | SelectionActionsCapability
    | undefined => capRegContainer.get(SelectionActionsCapability)();

  const markupCapability = (): MarkupCapability | undefined =>
    capabilityRegistry.get(MarkupCapability)();

  const isInStationNavigation = createMemo(
    () =>
      viewModeCapCoord()
        ?.stationNavigationController()
        ?.selectedStationIndex() != null
  );

  // The top floating toolbar hosts the per-pane visibility dropdown plus
  // the 3D-only camera controls. It stays visible
  // in every view mode so the visibility dropdown is always reachable — in
  // 2D map mode the dropdown surfaces the frustum / stationMarkers /
  // navigationPath toggles (see `buildRowsForViewMode`), while the 3D-only
  // controls inside are gated separately (see `viewModeType() === '3D'`
  // below) so they don't clutter 2D / panorama / image panes, where there's
  // no camera-mode switching.
  const isFloatingToolbarVisible = createMemo(() => viewModeType() != null);

  // Show station-nav whenever its controller is ready *and* the pane is
  // currently in an image view, or it's a 3D pane that's mid-station-
  // navigation (the close button leaves station-nav mode in 3D only).
  const isStationNavBarVisible = createMemo(() => {
    if (!viewModeCapCoord()?.stationNavigationController()) return false;
    return (
      viewModeType() === 'Image' ||
      viewModeType() === 'Panorama' ||
      capRegContainer
        .get(StationsNavigationCapability)()
        ?.selectedStationIndex() != null
    );
  });

  const viewToolActions = createRotatePanoramasAction({
    viewModeType,
    planarContents: () => props.data.planarContents(),
  });

  createEffect(
    on(allowMarkupEditing, (allowed) => {
      if (allowed) {
        return;
      }
      const mc = markupCapability();
      if (!mc?.isActive()) {
        return;
      }
      capabilityRegistry
        .get(MarkupsCapabilityProvider)()
        ?.discardPendingMarkups();
      mc.deactivate();
    })
  );

  const isAlignmentMeasurementInteractionAllowed = createMemo(() => {
    if (viewModeType() !== '3D') {
      return false;
    }
    return hasLoadedAlignmentInProject(dataState);
  });

  const isDrawMarkupCreationAllowed = createMemo(() => {
    if (viewModeType() !== '3D') {
      return false;
    }
    const pcProvider = capRegContainer.get(PointCloudCapabilityProvider)();
    if (!pcProvider) {
      return false;
    }
    return pcProvider.items().length > 0 && pcProvider.isPointCloudsVisible();
  });

  const isImageLoading = createMemo(() => {
    const vm = viewModeCapCoord()?.viewMode();
    if (vm?.type !== 'Image' && vm?.type !== 'Panorama') return false;

    const imageCaps = capRegContainer.get(
      CapabilitiesCollection<ImageCapabilityBase>
    )();
    if (!imageCaps) return false;

    const cameraIndex = vm.type === 'Image' ? vm.cameraIndex : 0;
    return imageCaps.capabilities[cameraIndex]?.isImageLoading() ?? false;
  });

  const { isHovered, ref: hoverRef } = useHover();
  const keyDownEvent = useKeyDownEvent();

  // Component-scope + keyed on the viewer signal so it re-runs for every pane when
  // the preferences change.
  createEffect(() => {
    const activeViewer = viewer();
    if (!activeViewer) return;

    applyViewerPreferences(
      activeViewer.settings,
      preferenceSettings(),
      viewerPreferenceMaterialPhong
    );
  });

  onMount(() => {
    session = createPaneViewerSession({
      placeholder,
      web3DPath: props.web3DPath,
      capRegContainer,
      setViewer,
      isAlignmentMeasurementInteractionAllowed,
      isDrawMarkupCreationAllowed,
      logger,
      cadEditingSession,
      allowMarkups,
      allowMarkupEditing,
      capabilityRegistry,
      viewMode: {
        initial: viewModeFromLayout(),
        dataLoadingService,
        web3dSettings,
        fetchGeoDataFiles: (csib64) =>
          fetchGeoDataFilesByCsib(coordinatesClient, csib64),
        datasetCsib: currentLoadedCsib,
        datasetCenter: currentLoadedCenter,
        datasetBounds: currentLoadedBounds,
        mapTileConfig,
      },
      runtime: {
        isInStationNavigation,
        viewModeType,
        isHovered,
        keyDownEvent,
        imagingApi,
        tilesetPrefetchContext,
      },
      registerSyncedPane: props.registerSyncedPane,
    });
    viewerWrapper = session.viewerWrapper;
    const viewModeCoordinator = session.viewModeCoordinator;
    setViewModeCapCoord(viewModeCoordinator);

    setEmptyViewerStateProps({
      viewMode: () => viewModeCoordinator.viewMode(),
      cameraIndex: () => viewModeCoordinator.currentCameraIndex(),
      datasetCsib: currentLoadedCsib,
      pcProvider: () => capRegContainer.get(PointCloudCapabilityProvider)(),
      trbProvider: () => capRegContainer.get(TrimBimCapabilityProvider)(),
      imagePointcloudCap: () =>
        capRegContainer.get(ImagePointcloudCapability)(),
      imageCaps: () =>
        capRegContainer.get(CapabilitiesCollection<ImageCapabilityBase>)(),
      stationsProvider: () =>
        capabilityRegistry.get(StationsCapabilityProvider)(),
      backgroundTopColor: () => preferenceSettings().backgroundTopColor,
      backgroundBottomColor: () => preferenceSettings().backgroundBottomColor,
    });
  });

  onCleanup(() => {
    session?.cleanup();
  });

  return (
    <div class="relative w-full h-full overflow-hidden group" ref={hoverRef}>
      <div
        class="absolute inset-0 w-full h-full overflow-hidden"
        ref={(x) => {
          placeholder = x;
        }}
      />
      <Show when={emptyViewerStateProps()}>
        <EmptyViewerState
          {...emptyViewerStateProps()!}
          class="absolute inset-0 z-content"
        />
      </Show>

      <Show when={isImageLoading()}>
        <div
          class="pointer-events-none absolute inset-0 z-header flex items-center justify-center"
          data-testid="image-texture-loading-spinner"
        >
          <modus-wc-loader size="lg" variant="spinner" />
        </div>
      </Show>

      <Show when={viewer()}>
        <Show when={cadEditingMode() && cadEditingSession()} keyed>
          {(session) => (
            <CadEditingToolbar
              activeTool={session.activeTool()}
              disabled={cadToolsDisabled()}
              onToggleTool={(tool: DrawingTool) =>
                capRegContainer.get(CadEditingCapability)()?.toggleTool(tool)
              }
            />
          )}
        </Show>
        <Show when={isFloatingToolbarVisible()}>
          <ToolbarPositioner anchor={Anchor.TopCenter} autoHide>
            <TrbToolbar orientation="horizontal" addSeparators={false}>
              {/* Camera controls only apply in 3D — there's
                  no camera-mode switching in image / panorama
                  panes — so they're hidden there. The visibility dropdown
                  below stays available in every view mode. */}
              <Show when={viewModeType() === '3D'}>
                <CameraControlSelector
                  alignmentNavigationCapability={
                    capRegContainer.get(AlignmentNavigationCapability)()!
                  }
                  freeViewCapability={
                    capRegContainer.get(FreeViewCapability)()!
                  }
                />
                <Show when={selectionActionsCapability()}>
                  <div
                    data-testid="main-toolbar-show-all-models"
                    class="contents"
                  >
                    <BasicButton
                      iconName="tc-icon-show-all"
                      tooltip={t('web3D.toolbar.actions.showAll')}
                      action={() =>
                        void selectionActionsCapability()?.showAll()
                      }
                      size="small"
                    />
                  </div>
                </Show>
              </Show>
              <Show when={isCadEditingEligible()}>
                <div
                  data-testid="floating-cad-editing-toolbar-toggle"
                  class="contents"
                >
                  <BasicButton
                    iconName="tc-icon-map"
                    tooltip={t('mobileMapping.cadEditing.toggle')}
                    action={toggleCadEditingMode}
                    pressed={cadEditingMode()}
                    size="small"
                  />
                </div>
              </Show>
              <PaneToolbarVisibilityControl
                capRegContainer={capRegContainer}
                viewMode={() =>
                  viewModeCapCoord()?.viewMode() ?? { type: '3D' as const }
                }
              />
            </TrbToolbar>
          </ToolbarPositioner>
        </Show>
        <Show when={capRegContainer.get(AlignmentNavigationCapability)()}>
          <ToolbarPositioner anchor={Anchor.TopRight} autoHide>
            <AlignmentNavigationToolbar
              alignmentNavigationCapability={capRegContainer.get(
                AlignmentNavigationCapability
              )()}
              lengthUnit={formattingSettings().lengthUnit as LengthUnit}
              lengthDecimals={formattingSettings().lengthDecimals}
            />
          </ToolbarPositioner>
        </Show>
        <PaneBottomBar
          viewModeCoordinator={viewModeCapCoord}
          viewToolActions={viewToolActions}
          isStationNavVisible={isStationNavBarVisible}
          showStationNavClose={() =>
            viewModeType() !== 'Image' && viewModeType() !== 'Panorama'
          }
          allowPopup={props.allowPopup}
        />
        <Show when={viewModeType() === '3D'}>
          <ToolbarPositioner
            anchor={Anchor.BottomCenter}
            customClasses={isStationNavBarVisible() ? '!bottom-[4.5rem]' : ''}
          >
            <ObjectSelectionToolbarContainer
              capabilityRegistryContainer={capRegContainer}
              customClasses="!p-1"
            />
          </ToolbarPositioner>
        </Show>
      </Show>
    </div>
  );
};
